import { addWorkdays } from "../misc/workdays";
import { addDaysKey, osloDateKey } from "../misc/osloDate";
import type { DealLine, DealLineInterval, MarkDealWonLine } from "../types";

// Produktlinjer og avtaleverdier (pipeline v2.1, kap. 9.2). Speiler
// deal_line_monthly og mark_deal_won i 20261018150000_pipeline_vunnet.sql.

export const DEAL_LINE_INTERVALS: DealLineInterval[] = [
  "one_time",
  "monthly",
  "quarterly",
  "yearly",
];

export const BINDING_MONTH_CHOICES = [0, 12, 24, 36] as const;

type LineAmount = Pick<DealLine, "quantity" | "unit_price" | "interval">;

const round2 = (value: number) => Math.round(value * 100) / 100;

export const lineAmount = (line: Pick<DealLine, "quantity" | "unit_price">) =>
  Number(line.quantity ?? 0) * Number(line.unit_price ?? 0);

// Gjentakende linjer omregnet til per måned.
export const monthlyAmount = (interval: DealLineInterval, amount: number) =>
  interval === "monthly"
    ? amount
    : interval === "quarterly"
      ? amount / 3
      : interval === "yearly"
        ? amount / 12
        : 0;

export const dealLineTotals = (lines: LineAmount[]) => {
  let oneTime = 0;
  let monthly = 0;
  for (const line of lines) {
    const amount = lineAmount(line);
    if (line.interval === "one_time") oneTime += amount;
    else monthly += monthlyAmount(line.interval, amount);
  }
  return { oneTime: round2(oneTime), monthly: round2(monthly) };
};

// Første år: engangs + 12 × månedlig (årsverdien i rapportene).
export const firstYearValue = (oneTime: number, monthly: number) =>
  round2(oneTime + 12 * monthly);

// Over bindingstiden: engangs + bindingstid × månedlig. null uten binding.
export const contractValue = (
  oneTime: number,
  monthly: number,
  bindingMonths: number | null | undefined,
) => (bindingMonths == null ? null : round2(oneTime + bindingMonths * monthly));

export const isRecurring = (line: Pick<DealLine, "interval">) =>
  line.interval !== "one_time";

export const toWonLines = (lines: DealLine[]): MarkDealWonLine[] =>
  lines
    .filter((line) => line.product_id != null)
    .map((line) => ({
      product_id: line.product_id!,
      description: line.description,
      quantity: Number(line.quantity),
      unit_price: Number(line.unit_price),
      interval: line.interval,
    }));

// Første dag i neste måned (YYYY-MM-01).
export const nextMonthStart = (fromKey: string = osloDateKey()) => {
  const [y, m] = fromKey.split("-").map(Number);
  const year = m === 12 ? y + 1 : y;
  const month = m === 12 ? 1 : m + 1;
  return `${year}-${String(month).padStart(2, "0")}-01`;
};

export const monthStart = (key: string) => `${key.slice(0, 7)}-01`;

// Planlagt oppstart: første virkedag om minst 5 virkedager (Europe/Oslo).
export const defaultPlannedStart = (fromKey: string = osloDateKey()) =>
  addWorkdays(fromKey, 5);

export const plannedLaunch = (startKey: string, durationWorkdays: number) =>
  addWorkdays(startKey, durationWorkdays);

// Abonnement fra: måneden etter planlagt lansering, ellers neste måned.
export const defaultSubscriptionStart = (launchKey?: string | null) =>
  launchKey ? nextMonthStart(launchKey) : nextMonthStart();

export const dayBefore = (key: string) => addDaysKey(key, -1);
